import { isCancelledError, QueryClient, QueryKey } from '@tanstack/react-query';

export const SLOW_REQUEST_MS = 2000;

type Track = (properties: Record<string, unknown>) => void;

// Only the first key segment: later segments may carry ids, phones or search text.
const keyName = (key: QueryKey | undefined) => String(key?.[0] ?? 'unknown');

// Long frames are kept this long; older requests under-report blocking.
const HISTORY_MS = 10 * 60 * 1000;

/**
 * Records long animation frames (main thread busy >50ms) as [start, end] pairs on the
 * performance timeline (not Date.now(): in tabs open for hours the two drift apart).
 * Returns undefined when the browser can't observe them (Firefox, Safari).
 */
function observeLongFrames():
  ((start: number, end: number) => number) | undefined {
  if (
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')
  ) {
    return undefined;
  }
  const frames: [number, number][] = [];
  new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      frames.push([entry.startTime, entry.startTime + entry.duration]);
    }
    const cutoff = performance.now() - HISTORY_MS;
    while (frames.length && frames[0][1] < cutoff) frames.shift();
  }).observe({ type: 'long-animation-frame', buffered: true });

  // Milliseconds of [start, end] during which the main thread was blocked.
  return (start, end) =>
    frames.reduce(
      (sum, [fs, fe]) =>
        sum + Math.max(0, Math.min(end, fe) - Math.max(start, fs)),
      0
    );
}

/**
 * Reports every query/mutation that takes >= SLOW_REQUEST_MS or fails, so slowness
 * sellers feel is measurable without waiting for complaints. Duration includes retries.
 * main_thread_blocked_ms is how much of that duration the page itself was busy:
 * responses can't be handled while it is, so a high value means slow UI, not slow API.
 * Where the network/server part of the time went is in `api_fetch_slow`
 * (trackSlowFetches), measured per API call in the Fetch wrapper.
 */
export function trackSlowRequests(queryClient: QueryClient, track: Track) {
  const startedAt = new Map<string, number>();
  const blockedMs = observeLongFrames();

  const report = (
    kind: 'query' | 'mutation',
    key: QueryKey | undefined,
    durationMs: number,
    error: unknown
  ) => {
    if (error && isCancelledError(error)) return;
    if (!error && durationMs < SLOW_REQUEST_MS) return;
    const now = performance.now();
    track({
      kind,
      key: keyName(key),
      duration_ms: Math.round(durationMs),
      main_thread_blocked_ms:
        blockedMs && Math.round(blockedMs(now - durationMs, now)),
      outcome: error ? 'error' : 'slow',
      error: error instanceof Error ? error.message.slice(0, 200) : undefined,
      path:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  };

  queryClient.getQueryCache().subscribe((event) => {
    const hash = event.query.queryHash;
    if (event.type === 'removed') {
      startedAt.delete(hash);
      return;
    }
    if (event.type !== 'updated') return;
    const { action } = event;
    if (action.type === 'fetch') {
      startedAt.set(hash, Date.now());
      return;
    }
    if (action.type !== 'success' && action.type !== 'error') return;
    const start = startedAt.get(hash);
    if (start === undefined) return; // e.g. setQueryData, not a fetch
    startedAt.delete(hash);
    report(
      'query',
      event.query.queryKey,
      Date.now() - start,
      action.type === 'error' ? action.error : null
    );
  });

  queryClient.getMutationCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const { action, mutation } = event;
    if (action.type !== 'success' && action.type !== 'error') return;
    report(
      'mutation',
      mutation.options.mutationKey,
      Date.now() - mutation.state.submittedAt,
      action.type === 'error' ? action.error : null
    );
  });
}
